import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Sparkle, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const STEPS = [
  {
    key: "personality",
    title: "Which words describe you best?",
    subtitle: "Pick three. We'll use them as your style compass.",
    kind: "multi",
    max: 3,
    options: ["bold", "feminine", "minimalist", "edgy", "soft", "playful", "classic", "bohemian", "powerful", "romantic"],
  },
  {
    key: "perception",
    title: "How do you want people to perceive you?",
    subtitle: "One honest sentence. She's listening.",
    kind: "text",
  },
  {
    key: "aesthetic_words",
    title: "Your dream aesthetic, in three words.",
    subtitle: "Think moodboard, not mall.",
    kind: "tags",
  },
  {
    key: "style_icons",
    title: "Who do you style-stalk?",
    subtitle: "Celebs, influencers, fictional characters — all fair game.",
    kind: "tags",
  },
  {
    key: "favorite_brands",
    title: "Brands or stores you gravitate toward?",
    subtitle: "Optional. It helps Ava learn your price point and vibe.",
    kind: "tags",
  },
  {
    key: "lifestyle",
    title: "What does your week look like?",
    subtitle: "Pick every hat you wear.",
    kind: "multi",
    max: 6,
    options: ["student", "office", "work-from-home", "nightlife", "gym", "travel", "creative", "entrepreneur", "parent", "social events"],
  },
  {
    key: "climate",
    title: "Your climate?",
    subtitle: "So Ava doesn't suggest leather in Miami in July.",
    kind: "single",
    options: ["tropical", "warm year-round", "four seasons", "cold", "variable"],
  },
  {
    key: "preferred_fits",
    title: "How do you like things to fit?",
    subtitle: "You can love more than one.",
    kind: "multi",
    max: 4,
    options: ["tailored", "oversized", "fitted", "flowy", "cropped", "high-waisted", "relaxed"],
  },
  {
    key: "body_type",
    title: "Want to share your body type? (Optional)",
    subtitle: "Always optional. Ava never judges — it just helps with fit.",
    kind: "single",
    options: ["", "hourglass", "pear", "apple", "rectangle", "inverted triangle", "prefer not to say"],
    labels: { "": "Skip this" },
    optional: true,
  },
  {
    key: "comfort_level",
    title: "Comfort vs. boldness?",
    subtitle: "Slide where you usually land.",
    kind: "slider",
    min: 1, max: 10,
    left: "Total comfort",
    right: "Boldly experimental",
  },
  {
    key: "favorite_colors",
    title: "Colors you love wearing.",
    subtitle: "Pick as many as you want.",
    kind: "colors",
    options: ["black", "white", "cream", "beige", "camel", "chocolate", "olive", "sage", "emerald", "navy", "denim blue", "sky blue", "lavender", "dusty pink", "red", "burgundy", "mustard", "rust"],
  },
  {
    key: "avoided_colors",
    title: "Colors you rarely touch.",
    subtitle: "Optional. Ava will steer clear.",
    kind: "colors",
    options: ["black", "white", "cream", "beige", "camel", "olive", "sage", "emerald", "navy", "lavender", "dusty pink", "red", "burgundy", "mustard", "neon yellow", "orange", "hot pink"],
    optional: true,
  },
  {
    key: "pattern_preference",
    title: "Patterns or minimalism?",
    subtitle: "Or somewhere in between.",
    kind: "single",
    options: ["patterns lover", "minimal & solid", "both depending on mood"],
  },
];

const COLOR_SWATCHES = {
  black: "#0F0F0F", white: "#FFFFFF", cream: "#F5F2EB", beige: "#DCCDB6",
  camel: "#B88D5E", chocolate: "#4A2F1E", olive: "#707641", sage: "#C3D1BC",
  emerald: "#046A38", navy: "#1B263B", "denim blue": "#5A7AA8", "sky blue": "#A7CEEB",
  lavender: "#C7B8DB", "dusty pink": "#DDB3B0", red: "#B8342B", burgundy: "#601E23",
  mustard: "#D4A72C", rust: "#A85A2A", "neon yellow": "#E8FF3A", orange: "#E27D3F",
  "hot pink": "#E8418C",
};

export default function Onboarding() {
  const { setUser, user } = useAuth();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    personality: [], perception: "", aesthetic_words: [], style_icons: [], favorite_brands: [],
    lifestyle: [], climate: "", preferred_fits: [], body_type: "", comfort_level: 5,
    favorite_colors: [], avoided_colors: [], pattern_preference: "",
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const s = STEPS[step];
  const progress = ((step + 1) / STEPS.length) * 100;

  const update = (key, v) => setData((d) => ({ ...d, [key]: v }));

  const toggle = (key, val, max) => {
    setData((d) => {
      const arr = d[key] || [];
      if (arr.includes(val)) return { ...d, [key]: arr.filter((x) => x !== val) };
      if (max && arr.length >= max) return d;
      return { ...d, [key]: [...arr, val] };
    });
  };

  const canAdvance = () => {
    if (s.optional) return true;
    const v = data[s.key];
    if (s.kind === "text") return (v || "").trim().length > 2;
    if (s.kind === "tags") return (v || []).length > 0;
    if (s.kind === "multi" || s.kind === "colors") return (v || []).length > 0;
    if (s.kind === "single") return !!v;
    if (s.kind === "slider") return true;
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const { data: updated } = await api.put("/users/me/profile", { profile: data, completed: true });
      setUser({ ...updated });
      toast.success("Your style profile is set. Let's play.");
      navigate("/app");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const next = () => {
    if (step === STEPS.length - 1) submit();
    else setStep(step + 1);
  };
  const prev = () => setStep(Math.max(0, step - 1));

  return (
    <div className="min-h-[100dvh] bg-cream-50 flex flex-col">
      <header className="px-6 sm:px-10 py-6 flex items-center justify-between border-b border-cream-200">
        <div className="flex items-center gap-2">
          <Sparkle weight="fill" size={20} />
          <span className="font-serif text-xl">Ava</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-xs uppercase tracking-[0.25em] text-charcoal-500">
            Step {step + 1} / {STEPS.length}
          </div>
          <div className="hidden sm:block w-40 h-[3px] bg-cream-200 rounded-full overflow-hidden">
            <div className="h-full bg-charcoal-700 transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-start sm:items-center justify-center px-6 sm:px-10 py-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-3xl"
          >
            <p className="text-[0.7rem] tracking-[0.3em] uppercase text-charcoal-500">Style profile</p>
            <h1 className="font-serif text-3xl sm:text-5xl lg:text-6xl mt-4 text-charcoal-900 tracking-tight">{s.title}</h1>
            <p className="mt-4 text-charcoal-600 text-base sm:text-lg max-w-xl">{s.subtitle}</p>

            <div className="mt-10">
              {s.kind === "multi" && (
                <div className="flex flex-wrap gap-3" data-testid={`step-${s.key}`}>
                  {s.options.map((o) => {
                    const sel = data[s.key].includes(o);
                    return (
                      <button
                        key={o}
                        type="button"
                        onClick={() => toggle(s.key, o, s.max)}
                        data-testid={`option-${s.key}-${o}`}
                        className={`rounded-full border px-5 py-2.5 text-sm transition-all ${
                          sel ? "bg-charcoal-700 text-white border-charcoal-700" : "bg-cream-100 border-cream-200 text-charcoal-700 hover:-translate-y-0.5"
                        }`}
                      >
                        {o}
                      </button>
                    );
                  })}
                </div>
              )}

              {s.kind === "single" && (
                <div className="flex flex-wrap gap-3">
                  {s.options.map((o) => {
                    const label = s.labels?.[o] || (o || "—");
                    const sel = data[s.key] === o;
                    return (
                      <button
                        key={o || "none"}
                        type="button"
                        onClick={() => update(s.key, o)}
                        data-testid={`option-${s.key}-${o || "skip"}`}
                        className={`rounded-full border px-5 py-2.5 text-sm transition-all ${
                          sel ? "bg-charcoal-700 text-white border-charcoal-700" : "bg-cream-100 border-cream-200 text-charcoal-700 hover:-translate-y-0.5"
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}

              {s.kind === "text" && (
                <textarea
                  value={data[s.key]}
                  onChange={(e) => update(s.key, e.target.value)}
                  data-testid={`input-${s.key}`}
                  rows={3}
                  className="w-full bg-transparent border-0 border-b-2 border-cream-200 focus:border-charcoal-700 focus:outline-none py-3 text-xl font-serif italic"
                  placeholder="e.g. Effortlessly put-together, like I have my life figured out…"
                />
              )}

              {s.kind === "tags" && (
                <TagInput value={data[s.key]} onChange={(v) => update(s.key, v)} testKey={s.key} />
              )}

              {s.kind === "slider" && (
                <div>
                  <input
                    type="range"
                    min={s.min} max={s.max}
                    value={data[s.key]}
                    onChange={(e) => update(s.key, parseInt(e.target.value, 10))}
                    data-testid={`slider-${s.key}`}
                    className="w-full accent-charcoal-700"
                  />
                  <div className="flex items-center justify-between mt-3 text-sm text-charcoal-600">
                    <span>{s.left}</span>
                    <span className="font-serif text-2xl text-charcoal-800">{data[s.key]}</span>
                    <span>{s.right}</span>
                  </div>
                </div>
              )}

              {s.kind === "colors" && (
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                  {s.options.map((c) => {
                    const sel = data[s.key].includes(c);
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => toggle(s.key, c)}
                        data-testid={`color-${s.key}-${c.replace(/\s/g, "-")}`}
                        className={`group rounded-2xl border p-3 flex flex-col items-center gap-2 transition-all ${
                          sel ? "border-charcoal-700 bg-cream-100 -translate-y-0.5" : "border-cream-200 bg-white hover:-translate-y-0.5"
                        }`}
                      >
                        <span
                          className="w-10 h-10 rounded-full border border-cream-200"
                          style={{ background: COLOR_SWATCHES[c] || "#999" }}
                        />
                        <span className="text-xs text-charcoal-600">{c}</span>
                        {sel && <CheckCircle size={14} weight="fill" className="text-charcoal-700" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <footer className="px-6 sm:px-10 py-6 border-t border-cream-200 flex items-center justify-between bg-cream-50">
        <button
          onClick={prev}
          disabled={step === 0}
          data-testid="onboarding-back"
          className="btn-outline-pill disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ArrowLeft size={16} className="mr-2" /> Back
        </button>
        <button
          onClick={next}
          disabled={!canAdvance() || saving}
          data-testid="onboarding-next"
          className="btn-primary-pill disabled:opacity-40"
        >
          {step === STEPS.length - 1 ? (saving ? "Saving…" : "Finish") : "Continue"}
          <ArrowRight size={16} className="ml-2" />
        </button>
      </footer>
    </div>
  );
}

function TagInput({ value = [], onChange, testKey }) {
  const [v, setV] = useState("");
  const add = () => {
    const t = v.trim();
    if (!t) return;
    if (!value.includes(t)) onChange([...value, t]);
    setV("");
  };
  return (
    <div>
      <div className="flex gap-2 mb-4">
        <input
          value={v}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          data-testid={`tag-input-${testKey}`}
          className="flex-1 bg-transparent border-0 border-b-2 border-cream-200 focus:border-charcoal-700 focus:outline-none py-2 text-lg"
          placeholder="Type and press Enter"
        />
        <button type="button" onClick={add} className="btn-outline-pill py-2 px-4 text-sm">Add</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {value.map((t) => (
          <button
            key={t}
            onClick={() => onChange(value.filter((x) => x !== t))}
            className="rounded-full bg-charcoal-700 text-white px-4 py-1.5 text-sm hover:opacity-80"
          >
            {t} ×
          </button>
        ))}
      </div>
    </div>
  );
}
